/**
 * The 12-column visual grid, one adapter per `VisualSpec.kind` (SPEC §9.4).
 *
 * M3 ships three of the ten kinds: `table`, `line`, and `kpi_row` (§14 asks
 * for two adapters plus the table). The remaining seven arrive at M7.
 *
 * A kind with no adapter renders a **placeholder that names it and says when
 * it lands** — not a blank space. A visual that silently disappears is a
 * report quietly showing less than its author declared, and the reader has no
 * way to know a chart was supposed to be there.
 */

import { lazy, Suspense, useMemo } from 'react'

import { barOption } from './adapters/bar'
import { comboOption } from './adapters/combo'
import { lineOption } from './adapters/line'
import { KpiRow } from './adapters/KpiRow'
import { DataTable } from './DataTable'
import { useTheme } from '../lib/theme'
import type { Cell, ColumnSpec, DatasetResult, VisualKind, VisualSpec } from '../lib/types'

/** Lazy: a table-only report should not pay for the charting library. */
const Chart = lazy(() => import('./Chart'))

/** Kinds with an adapter today. Everything else gets the placeholder. */
export const IMPLEMENTED_KINDS: ReadonlySet<VisualKind> = new Set<VisualKind>([
  'table',
  'line',
  'bar',
  'combo',
  'kpi_row',
])

/**
 * The cartesian adapters, dispatched by kind. `stacked_bar`, `area`, `pie`,
 * `scatter` and `heatmap` are intentionally absent: no report in the registry
 * declares them, and §14 says name the report before building the adapter.
 */
const CARTESIAN_OPTION = {
  line: lineOption,
  bar: barOption,
  combo: comboOption,
} as const

type CartesianKind = keyof typeof CARTESIAN_OPTION

function isCartesian(kind: VisualKind): kind is CartesianKind {
  return kind in CARTESIAN_OPTION
}

/** Where the rest arrive, per §14. Shown to the reader, so it stays accurate. */
const ARRIVES_IN = 'M7'

export interface VisualHostProps {
  readonly visuals: readonly VisualSpec[]
  readonly columns: readonly ColumnSpec[]
  readonly rows: readonly (readonly Cell[])[]
  /**
   * The active department's accent (§9.2), the served value from `/api/me` or
   * `/api/departments` — not a department key, so a recolour in the admin panel
   * reaches the first chart series. The neutral sequence follows it.
   */
  readonly accent: string
  readonly reportTitle: string
  /** Additional named datasets from the response (§8.6). */
  readonly datasets?: Readonly<Record<string, DatasetResult>>
  /** Called when a visual with `selects` is clicked (§9.5). */
  readonly onSelect?: (param: string, value: Cell) => void
}

/**
 * The data a visual reads.
 *
 * A visual names one dataset and reads only that one. Falling back to the
 * primary dataset when a named one is missing would render a panel full of the
 * wrong numbers, which is worse than rendering nothing — so it returns null and
 * the caller says so.
 */
function dataFor(
  visual: VisualSpec,
  columns: readonly ColumnSpec[],
  rows: readonly (readonly Cell[])[],
  datasets: Readonly<Record<string, DatasetResult>>,
): { columns: readonly ColumnSpec[]; rows: readonly (readonly Cell[])[] } | null {
  if (visual.dataset === null || visual.dataset === PRIMARY_DATASET) return { columns, rows }
  const dataset = datasets[visual.dataset]
  if (dataset === undefined) return null
  return { columns: dataset.columns, rows: dataset.rows }
}

const PRIMARY_DATASET = 'primary'

function MissingDataset({ visual }: { visual: VisualSpec }) {
  return (
    <div className="flex min-h-32 flex-col items-center justify-center rounded-[var(--radius-lg)] border border-dashed border-[var(--color-negative)]/50 p-6 text-center">
      <p className="text-[length:var(--text-sm)] text-[var(--color-negative)]">
        {visual.title ?? visual.id}
      </p>
      <p className="mt-1 text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
        This panel reads dataset <code className="numeric">{visual.dataset}</code>, which the
        report did not return.
      </p>
    </div>
  )
}

function Placeholder({ visual }: { visual: VisualSpec }) {
  return (
    <div className="flex min-h-32 flex-col items-center justify-center rounded-[var(--radius-lg)] border border-dashed border-[var(--color-rule)] p-6 text-center">
      <p className="text-[length:var(--text-sm)] text-[var(--color-ink-muted)]">
        {visual.title ?? visual.id}
      </p>
      <p className="mt-1 text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
        The <code className="numeric">{visual.kind}</code> visual arrives in {ARRIVES_IN}. The
        figures below are complete.
      </p>
    </div>
  )
}

function ChartVisual({
  visual,
  kind,
  columns,
  rows,
  accent,
  onSelectPoint,
}: {
  visual: VisualSpec
  kind: CartesianKind
  columns: readonly ColumnSpec[]
  rows: readonly (readonly Cell[])[]
  accent: string
  onSelectPoint?: ((value: string | number) => void) | undefined
}) {
  // Subscribing repaints this component when the theme flips; `theme` is in the
  // memo deps for the same reason. The adapters read the chrome colours (axis
  // labels, grid lines) from CSS custom properties at build time, and a canvas
  // cannot inherit a variable — so without rebuilding the option on a toggle
  // the chart would keep the previous palette. The bespoke SVG charts (CAR/RFT)
  // need none of this: they reference `var(--…)` directly.
  const { theme } = useTheme()
  const option = useMemo(
    // `theme` is a deliberate trigger, not an argument: it forces the option to
    // rebuild (and re-read the CSS vars) when the palette flips.
    () => CARTESIAN_OPTION[kind](visual, columns, rows, accent),
    [kind, visual, columns, rows, accent, theme],
  )
  return (
    <Suspense fallback={<div className="h-[280px] animate-pulse rounded bg-[var(--color-rule)]/40" />}>
      <Chart
        option={option}
        ariaLabel={visual.title ?? `${visual.kind} chart`}
        onSelectPoint={onSelectPoint}
      />
    </Suspense>
  )
}

export function VisualHost({
  visuals,
  columns,
  rows,
  accent,
  reportTitle,
  datasets = {},
  onSelect,
}: VisualHostProps) {
  if (visuals.length === 0) {
    // A report with no declared visual still has to show its numbers.
    return <DataTable columns={columns} rows={rows} caption={reportTitle} />
  }

  return (
    <div className="grid grid-cols-12 gap-4">
      {visuals.map((visual) => {
        const span = visual.kind === 'kpi_row' ? 12 : visual.span
        const data = dataFor(visual, columns, rows, datasets)
        return (
          <section
            key={visual.id}
            className={span === 6 ? 'col-span-12 lg:col-span-6' : 'col-span-12'}
          >
            {visual.title !== null && visual.kind !== 'table' && (
              <h3 className="mb-2 text-[length:var(--text-sm)] font-medium text-[var(--color-ink)]">
                {visual.title}
              </h3>
            )}

            {data === null ? (
              <MissingDataset visual={visual} />
            ) : visual.kind === 'table' ? (
              <DataTable
                columns={data.columns}
                rows={data.rows}
                caption={visual.title ?? reportTitle}
              />
            ) : visual.kind === 'kpi_row' ? (
              <KpiRow visual={visual} columns={data.columns} rows={data.rows} />
            ) : isCartesian(visual.kind) ? (
              <div className="rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-[var(--color-surface-raised)] p-3 elevated">
                <ChartVisual
                  visual={visual}
                  kind={visual.kind}
                  columns={data.columns}
                  rows={data.rows}
                  accent={accent}
                  onSelectPoint={
                    visual.selects === null || onSelect === undefined
                      ? undefined
                      : (value) => {
                          onSelect(visual.selects as string, value)
                        }
                  }
                />
              </div>
            ) : (
              <Placeholder visual={visual} />
            )}
          </section>
        )
      })}
    </div>
  )
}
